<template>
  <ElForm
    ref="campaignInfoForm"
    :model="campaignInfoModel"
    :rules="campaignInfoRules"
    :disabled="isLoading"
    label-position="top"
    @submit.native.prevent="updateCampaignInfo"
  >
    <ElFormItem :label="$t('ADVERTISEMENT.CAMPAIGNS.NAME')" prop="name">
      <ElInput v-model="campaignInfoModel.name" dir="ltr" />
    </ElFormItem>
    <ElFormItem
      prop="startDate"
      :label="$t('ADVERTISEMENT.CAMPAIGNS.START_DATE')"
    >
      <UiDatePickerEndFuture
        :default-date="getDateValue(campaignInfoModel.startDate)"
        @select-date="setStartDate"
      />
    </ElFormItem>
    <ElFormItem prop="endDate" :label="$t('ADVERTISEMENT.CAMPAIGNS.END_DATE')">
      <UiDatePickerEndFuture
        :default-date="getDateValue(campaignInfoModel.endDate)"
        @select-date="setEndDate"
      />
    </ElFormItem>
    <ElFormItem :label="$t('ADVERTISEMENT.CAMPAIGNS.STATUS')" prop="paused">
      <AlternativeSelect
        key="value"
        v-model="campaignInfoModel.paused"
        :items="statusOptions"
      />
    </ElFormItem>
    <ElFormItem>
      <ElCheckbox
        v-model="campaignInfoModel.hasTracker"
        @input="handleHasTrackerChange"
      >
        {{ $t('ADVERTISEMENT.CAMPAIGNS.CHOOSE_TO_HAVE_TRACKER') }}
      </ElCheckbox>
    </ElFormItem>

    <ElCollapseTransition>
      <div v-show="campaignInfoModel.hasTracker">
        <ElFormItem
          :label="$t('ADVERTISEMENT.CAMPAIGNS.IMPRESSION_TRACKER_LINK')"
          prop="impressionTrackerLink"
        >
          <ElInput
            v-model="campaignInfoModel.impressionTrackerLink"
            type="url"
            dir="ltr"
          />
          <InputHelper type="warning" el-icon="el-icon-warning">
            {{ $t('ADVERTISEMENT.CAMPAIGNS.AVAILABLE_TRACKERS') }}
          </InputHelper>
        </ElFormItem>
      </div>
    </ElCollapseTransition>

    <ElButton
      :loading="isLoading"
      type="primary"
      native-type="submit"
      size="large"
    >
      {{ $t('SAVE') }}
    </ElButton>
    <ElButton type="text" @click.native="$emit('cancel')">
      {{ $t('CANCEL') }}
    </ElButton>
  </ElForm>
</template>

<script>
import { dateFormat } from '@/utils/date';
import { SearchAdsCampaigns } from '@/api/api';
import UiDatePickerEndFuture from '@@/common/UiDatePickerEndFuture';
import InputHelper from '@@/common/InputHelper';
import AlternativeSelect from '@@/common/AlternativeSelect';
import form from '@/mixins/form';
import campaignId from '@/mixins/advertisement/campaignId';
import required from '@/utils/validation-rules/required';

export default {
  name: 'CampaignInfoForm',
  components: {
    UiDatePickerEndFuture,
    InputHelper,
    AlternativeSelect,
  },
  mixins: [form, campaignId],
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      campaignInfoModel: {
        name: this.data.name,
        packageName: this.data.packageName,
        startDate: this.data.startDate,
        endDate: this.data.endDate,
        paused: this.data.paused,
        impressionTrackerLink: this.data.impressionTrackerLink,
        hasTracker: this.data.hasTracker,
      },
    };
  },
  computed: {
    statusOptions() {
      return [
        {
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.ACTIVE'),
          value: false,
        },
        {
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.PAUSED'),
          value: true,
        },
      ];
    },
    campaignInfoRules() {
      const requiredRule = required(this.$t('VALIDATION_REQUIRED_ERR'));
      return {
        name: requiredRule,
        startDate: requiredRule,
        endDate: requiredRule,
        impressionTrackerLink: {
          type: 'url',
          message: this.$t('VALIDATION.URL_IS_INVALID'),
          trigger: 'change',
        },
      };
    },
  },
  methods: {
    handleHasTrackerChange(hasTracker) {
      if (!hasTracker) {
        this.resetTracker();
      }
    },
    updateCampaignInfo() {
      if (!this.isValid('campaignInfoForm')) {
        return false;
      }

      this.isLoading = true;

      return SearchAdsCampaigns.updateCampaign({
        campaignId: this.campaignId,
        campaignInfo: this.campaignInfoModel,
      })
        .then(message => {
          this.$message.success(message);
          this.$emit('update');
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    setStartDate(selectedDate) {
      this.campaignInfoModel.startDate = dateFormat()('YYYY-MM-DDT00:00:00')(
        selectedDate,
      );
    },

    setEndDate(selectedDate) {
      this.campaignInfoModel.endDate = dateFormat()('YYYY-MM-DDT23:59:59')(
        selectedDate,
      );
    },

    getDateValue(date) {
      return new Date(date).valueOf();
    },

    resetTracker() {
      this.campaignInfoModel.impressionTrackerLink = null;
    },
  },
};
</script>
